/* eslint-disable no-restricted-syntax --
 * admin-config.ts uses hardcoded title/description strings that are looked up
 * via t('admin:<id>.title', { defaultValue }) at consumption time (see
 * useAdminConfig). The i18n gate rule (HMB-18647) can't distinguish this
 * pattern from raw user-facing strings, so a file-level disable matches the
 * grandfathered state of every existing entry until this file is migrated to
 * inline t() keys or refactored away from the defaultValue pattern.
 */
import {
  BellDot,
  Blocks,
  Building,
  Building2,
  FileText,
  Globe,
  Inbox,
  MessageSquare,
  MessageSquareText,
  Settings as SettingsIcon,
  Star,
  User,
  Users2,
  Bell,
  Lock,
  Key,
} from 'lucide-react';
import React from 'react';

import { isWorkflowManagementUIEnabled } from '@app/helpers/featureFlags';
import {
  integrationPartnersLoader,
  partnerConfigLoader,
} from '@app/services/integration-config';

import {
  ADMIN_NOTIFICATION_CONFIG,
  FNOL_UI,
  ORG_SETTINGS_AUTO_MESSAGE_PREFIX,
  ORG_SETTINGS_USER_MESSAGE_PREFIX,
  SYSTEM_ADMIN,
  USER_MANAGEMENT_TAB,
  PARTNER_CONFIG_VIEWER,
  USER_MANAGEMENT,
  GROUP_MANAGEMENT,
  AUTO_OPT_IN_USERS,
  SERVICE_CASE_MANAGEMENT,
  CASE_AI_ASSISTANTS,
  MTLS_CONFIG_UI,
  QUEUES,
  LOCALIZATION_SETTINGS,
  MULTI_PARTY_TEXTING,
  OAUTH_2_UI,
  QUEUE_MANAGEMENT,
  IS_ALIAS_OPERATOR,
  ORG_FABRIC_SETTING_ENABLED,
} from './permissions';
import { SUPERVISOR } from './roles';

export const partnerEnabled = (partnerName: string): string =>
  `partnerEnabled(${partnerName})`;

export const isPartnerEnabledPermission = (permission: string): boolean =>
  permission.startsWith('partnerEnabled');

export const extractPartnerEnabledPermission = (permission: string): string => {
  if (!isPartnerEnabledPermission(permission)) return '';
  // Extract content between 'partnerEnabled(' and ')'
  const match = permission.match(/^partnerEnabled\((.*)\)$/);
  return match ? match[1].trim() : '';
};

// Helper function to create inverted permission (hides item if permission is present)
export const notPermission = (permission: string): string => `!${permission}`;

// Helper function to create OR permission (user needs at least one of the permissions)
// Usage: permissions: [anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB)]
export const anyOf = (...permissions: string[]): string =>
  `anyOf(${permissions.join('|')})`;

// Helper function to check if a permission string is an anyOf permission
export const isAnyOfPermission = (permission: string): boolean =>
  permission.startsWith('anyOf(');

// Helper function to extract permissions from an anyOf permission string
export const extractAnyOfPermissions = (permission: string): string[] => {
  if (!isAnyOfPermission(permission)) return [];
  // Extract content between 'anyOf(' and ')'
  const match = permission.match(/^anyOf\((.*)\)$/);
  return match ? match[1].split('|') : [];
};

// Helper function to create OR groups of AND conditions
// Usage: permissions: [orGroup([SYSTEM_ADMIN, FEATURE_FLAG_Y], [IS_ALIAS_OPERATOR])]
// Means: (SYSTEM_ADMIN AND FEATURE_FLAG_Y) OR (IS_ALIAS_OPERATOR)
export const orGroup = (...groups: string[][]): string =>
  `orGroup(${groups.map((g) => g.join('&')).join('|')})`;

// Helper function to check if a permission string is an orGroup permission
export const isOrGroupPermission = (permission: string): boolean =>
  permission.startsWith('orGroup(');

// Helper function to extract permission groups from an orGroup permission string
// Returns an array of arrays, where each inner array is a group of ANDed permissions
// Handles nested expressions like anyOf() by respecting parentheses depth
export const extractOrGroupPermissions = (permission: string): string[][] => {
  if (!isOrGroupPermission(permission)) return [];
  // Extract content between 'orGroup(' and ')'
  const match = permission.match(/^orGroup\((.*)\)$/);
  if (!match) return [];

  const content = match[1];
  const groups: string[][] = [];
  let currentGroup: string[] = [];
  let currentToken = '';
  let parenDepth = 0;

  // Parse character by character, tracking parentheses depth
  for (let i = 0; i < content.length; i++) {
    const char = content[i];

    if (char === '(') {
      parenDepth++;
      currentToken += char;
    } else if (char === ')') {
      parenDepth--;
      currentToken += char;
    } else if (char === '|' && parenDepth === 0) {
      // Top-level pipe - separates OR groups
      if (currentToken) {
        currentGroup.push(currentToken.trim());
        currentToken = '';
      }
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
        currentGroup = [];
      }
    } else if (char === '&' && parenDepth === 0) {
      // Top-level ampersand - separates AND conditions within a group
      if (currentToken) {
        currentGroup.push(currentToken.trim());
        currentToken = '';
      }
    } else {
      currentToken += char;
    }
  }

  // Don't forget the last token and group
  if (currentToken) {
    currentGroup.push(currentToken.trim());
  }
  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
};

// TODO: Remove isIntegrationPermission and extractIntegrationPermission once
// the partnerPermission() helper is confirmed removed from the base branch.
// No current AdminItems use the 'partnerPermission(...)' format, so these
// code paths are never triggered. Kept for backward compatibility during the
// consolidation PR (HMB-14845).
const isIntegrationPermission = (permission: string): boolean =>
  permission.startsWith('partnerPermission');

const extractIntegrationPermission = (
  permission: string,
): [string, string, boolean] => {
  // Extract content between 'partnerPermission(' and ')'
  const match = permission.match(/^partnerPermission\((.*)\)$/);
  const args = match ? match[1].split(',') : [];
  const trimmedArgs = args.map((arg) => arg?.trim());
  return [
    trimmedArgs[0],
    trimmedArgs[1],
    Boolean(Number.parseInt(trimmedArgs[2], 10)),
  ];
};

// Helper function to check if a permission requirement is satisfied
// This handles orGroup (OR of AND groups), anyOf (OR), inverted (NOT), and regular permissions
export const checkPermissionRequirement = (
  permission: string,
  userPermissions: string[],
  integrationPartnersMap: Record<string, boolean>,
): boolean => {
  // Handle orGroup permissions (OR of AND groups)
  // Each group is ANDed internally, then groups are ORed together
  if (isOrGroupPermission(permission)) {
    const groups = extractOrGroupPermissions(permission);
    return groups.some((group) =>
      group.every((p) =>
        checkPermissionRequirement(p, userPermissions, integrationPartnersMap),
      ),
    );
  }

  // Handle anyOf permissions (OR logic)
  if (isAnyOfPermission(permission)) {
    const requiredPermissions = extractAnyOfPermissions(permission);
    return requiredPermissions.some((p) => userPermissions.includes(p));
  }

  // Handle inverted permissions (NOT logic)
  if (permission.startsWith('!')) {
    const actualPermission = permission.slice(1);
    return !userPermissions.includes(actualPermission);
  }

  if (isPartnerEnabledPermission(permission)) {
    const partnerName = extractPartnerEnabledPermission(permission);
    return integrationPartnersMap[partnerName];
  }

  if (isIntegrationPermission(permission)) {
    const [requiredPermission, partnerName, shouldCheckIntegrationEnabled] =
      extractIntegrationPermission(permission);
    const isIntegrationEnabled = integrationPartnersMap[partnerName] ?? false;
    if (shouldCheckIntegrationEnabled === isIntegrationEnabled) {
      return checkPermissionRequirement(
        requiredPermission,
        userPermissions,
        integrationPartnersMap,
      );
    }
    return true;
  }

  // Regular permission (must have it)
  return userPermissions.includes(permission);
};

export const hasAccess = (
  config: AdminItem | null,
  userPermissionsArray: string[],
  integrationPartnersMap: Record<string, boolean>,
): boolean => {
  if (config?.permissions?.length) {
    return config.permissions.every((permission: string) =>
      checkPermissionRequirement(
        permission,
        userPermissionsArray,
        integrationPartnersMap,
      ),
    );
  }
  return true;
};

export const roleNotAllowed = (
  config: AdminItem | null,
  currentUserRoles: string[],
): boolean => {
  if (
    currentUserRoles.includes('ADMIN') ||
    currentUserRoles.includes('SYSTEM ADMIN')
  )
    return false;
  if (config?.disallowedRoles?.length) {
    return config?.disallowedRoles?.some((role: string) =>
      currentUserRoles.includes(role),
    );
  }
  return false;
};

export const getPermissionsToCheck = (
  config: AdminItem | null,
  integrationPartnersMap: Record<string, boolean>,
): string[] => {
  // const permissions = config?.permissions;
  if (!config?.permissions) return [];
  const allPermissions: string[] = [];
  const buildPermissionsArray = (permissions: string[]) =>
    permissions.forEach((permission: string) => {
      if (isOrGroupPermission(permission)) {
        // Extract all permissions from all groups within orGroup
        const groups = extractOrGroupPermissions(permission);
        groups.forEach((group) => buildPermissionsArray(group));
      } else if (isAnyOfPermission(permission)) {
        // Extract permissions from anyOf wrapper
        allPermissions.push(...extractAnyOfPermissions(permission));
      } else if (permission.startsWith('!')) {
        // Extract actual permission from inverted permission
        allPermissions.push(permission.slice(1));
      } else if (isPartnerEnabledPermission(permission)) {
        // eslint-disable-next-line no-useless-return
        return;
      } else if (isIntegrationPermission(permission)) {
        const [requiredPermission, partnerName, shouldCheckIntegrationEnabled] =
          extractIntegrationPermission(permission);
        const isIntegrationEnabled =
          integrationPartnersMap[partnerName] ?? false;
        if (shouldCheckIntegrationEnabled === isIntegrationEnabled)
          buildPermissionsArray([requiredPermission]);
      } else {
        // Regular permission
        allPermissions.push(permission);
      }
    });
  buildPermissionsArray(config?.permissions || []);
  return allPermissions;
};

export interface AdminItemDescriptionContext {
  isQueuesEnabled: boolean;
  t?: TranslateFn;
}

export type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

// Children are not recursively translated — callers that read them should re-translate via this helper.
export const translateAdminItem = (
  item: AdminItem,
  t: TranslateFn,
): AdminItem => ({
  ...item,
  title: t(`admin:${item.id}.title`, { defaultValue: item.title }),
  description: t(`admin:${item.id}.description`, {
    defaultValue: item.description,
  }),
});

export interface AdminItem {
  id: string;
  title: string;
  description: string;
  /** When set, used for card description; return undefined to use item.description. */
  getDescription?: (context: AdminItemDescriptionContext) => string | undefined;
  url: string;
  icon: React.ComponentType<{ size?: string | number }>;
  permissions?: string[];
  integrationName?: string;
  searchKeywords?: string[];
  tags?: string[];
  children?: AdminItem[];
  // Component path for dynamic lazy loading
  componentPath?: string;
  // Whether this item should render as a route (some items might be just navigation parents)
  hasRoute?: boolean;
  disallowedRoles?: string[];
  loader?: (args?: {
    params: Record<string, string | undefined>;
  }) => Promise<unknown>;
}

/** True if this admin item depends on integration/partner data. */
export const isPartnerItem = (item: AdminItem): boolean =>
  Boolean(
    item.integrationName ||
      item.permissions?.some(
        (p) => isPartnerEnabledPermission(p) || isIntegrationPermission(p),
      ),
  );

export interface AdminSection {
  id: string;
  title: string;
  description?: string;
  items: AdminItem[];
}

// Main admin route (landing page)
export const MAIN_ADMIN_ROUTE: AdminItem = {
  id: 'admin-main',
  title: 'Admin',
  description: 'Configure your preferences and system administration',
  url: '/admin',
  icon: SettingsIcon,
  componentPath: '@app/components/Admin/admin',
  hasRoute: true,
};

export const ADMIN_CONFIG: AdminSection[] = [
  {
    id: 'administration',
    title: 'Administration',
    description: 'Organization and system administration settings',
    items: [
      {
        id: 'organization',
        title: 'Organization',
        description: 'Configure organization-wide settings and policies',
        url: '/admin/organization',
        icon: Building,
        permissions: [SYSTEM_ADMIN],
        searchKeywords: [],
        componentPath: '@app/components/Admin/Organization/organization',
        hasRoute: true,
        children: [
          {
            id: 'message-prefixes',
            title: 'Message prefixes',
            description: 'Set up message prefixes for system and user messages',
            url: '/admin/organization/message-prefixes',
            icon: Building,
            permissions: [SYSTEM_ADMIN],
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/Organization/MessagePrefixes/message-prefixes',
            hasRoute: true,
            children: [
              {
                id: 'system-message-prefix',
                title: 'System message prefix',
                description:
                  'Configure system message prefix for automated messages',
                url: '/admin/organization/message-prefixes/system-message-prefix',
                icon: Building,
                permissions: [SYSTEM_ADMIN, ORG_SETTINGS_AUTO_MESSAGE_PREFIX],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/MessagePrefixes/SystemMessagePrefix/system-message-prefix',
                hasRoute: true,
              },
              {
                id: 'user-message-prefix',
                title: 'User message prefix',
                description:
                  'Configure title, case ID, and phone number prefixes for user messages',
                url: '/admin/organization/message-prefixes/user-message-prefix',
                icon: Building,
                permissions: [SYSTEM_ADMIN, ORG_SETTINGS_USER_MESSAGE_PREFIX],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/MessagePrefixes/UserMessagePrefix/user-message-prefix',
                hasRoute: true,
              },
            ],
          },
          {
            id: 'case-types',
            title: 'Case types',
            description: 'Manage case type configurations',
            url: '/admin/organization/case-types',
            icon: Building,
            permissions: [SYSTEM_ADMIN],
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/Organization/CaseTypes/case-types',
            hasRoute: true,
            children: [
              {
                id: 'claim',
                title: 'Claim',
                description: 'Configure claim case type settings',
                url: '/admin/organization/case-types/claim',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/CaseTypes/Claim/claim',
                hasRoute: true,
                children: [
                  {
                    id: 'claim-auto-close',
                    title: 'Auto Close Cases',
                    description:
                      'Configure time period without inbound messages to automatically close the case.',
                    url: '/admin/organization/case-types/claim/auto-close-cases',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['claim'],
                    searchKeywords: [
                      'auto',
                      'close',
                      'automatic',
                      'inactivity',
                    ],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Claim/AutoClose/auto-close',
                    hasRoute: true,
                  },
                  {
                    id: 'claim-welcome-message-not-opted-in',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for claims when not opted in',
                    url: '/admin/organization/case-types/claim/welcome-message-not-opted-in',
                    icon: Building,
                    permissions: [
                      SYSTEM_ADMIN,
                      notPermission(AUTO_OPT_IN_USERS),
                    ],
                    searchKeywords: [],
                    tags: ['claim', 'notOptedIn'],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Claim/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                    hasRoute: true,
                  },
                  {
                    id: 'claim-welcome-message-opted-in',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for claims when opted in',
                    url: '/admin/organization/case-types/claim/welcome-message-opted-in',
                    icon: Building,
                    permissions: [
                      SYSTEM_ADMIN,
                      notPermission(AUTO_OPT_IN_USERS),
                    ],
                    tags: ['claim', 'optedIn'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Claim/WelcomeMessageOptedIn/welcome-message-opted-in',
                    hasRoute: true,
                  },
                  {
                    id: 'claim-assignment-message',
                    title: 'Assignment message',
                    description: 'Configure assignment message for claims',
                    url: '/admin/organization/case-types/claim/assignment-message',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['claim'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Claim/AssignmentMessage/assignment-message',
                    hasRoute: true,
                  },
                  {
                    id: 'claim-reassignment-message',
                    title: 'Reassignment message',
                    description: 'Configure reassignment message for claims',
                    url: '/admin/organization/case-types/claim/reassignment-message',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['claim'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Claim/ReassignmentMessage/reassignment-message',
                    hasRoute: true,
                  },
                  {
                    id: 'claim-welcome-message-marley-opt-in-disabled',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for claims when Marley opt-in is disabled',
                    url: '/admin/organization/case-types/claim/welcome-message-marley-opt-in-disabled',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN, AUTO_OPT_IN_USERS],
                    tags: ['claim', 'marleyOptInDisabled'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Claim/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                    hasRoute: true,
                  },
                ],
              },
              {
                id: 'general',
                title: 'General',
                description: 'Configure general case type settings',
                url: '/admin/organization/case-types/general',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/CaseTypes/General/general',
                hasRoute: true,
                children: [
                  {
                    id: 'general-auto-close',
                    title: 'Auto Close Cases',
                    description:
                      'Configure time period without inbound messages to automatically close the case.',
                    url: '/admin/organization/case-types/general/auto-close-cases',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['general'],
                    searchKeywords: [
                      'auto',
                      'close',
                      'automatic',
                      'inactivity',
                    ],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/General/AutoClose/auto-close',
                    hasRoute: true,
                  },
                  {
                    id: 'general-welcome-message-not-opted-in',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for general cases when not opted in',
                    url: '/admin/organization/case-types/general/welcome-message-not-opted-in',
                    icon: Building,
                    permissions: [
                      SYSTEM_ADMIN,
                      notPermission(AUTO_OPT_IN_USERS),
                    ],
                    tags: ['general', 'notOptedIn'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/General/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                    hasRoute: true,
                  },
                  {
                    id: 'general-welcome-message-opted-in',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for general cases when opted in',
                    url: '/admin/organization/case-types/general/welcome-message-opted-in',
                    icon: Building,
                    permissions: [
                      SYSTEM_ADMIN,
                      notPermission(AUTO_OPT_IN_USERS),
                    ],
                    tags: ['general', 'optedIn'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/General/WelcomeMessageOptedIn/welcome-message-opted-in',
                    hasRoute: true,
                  },
                  {
                    id: 'general-reassignment-message',
                    title: 'Reassignment message',
                    description:
                      'Configure reassignment message for general cases',
                    url: '/admin/organization/case-types/general/reassignment-message',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['general'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/General/ReassignmentMessage/reassignment-message',
                    hasRoute: true,
                  },
                  {
                    id: 'general-welcome-message-marley-opt-in-disabled',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for general cases when Marley opt-in is disabled',
                    url: '/admin/organization/case-types/general/welcome-message-marley-opt-in-disabled',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN, AUTO_OPT_IN_USERS],
                    searchKeywords: [],
                    tags: ['general', 'marleyOptInDisabled'],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/General/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                    hasRoute: true,
                  },
                ],
              },
              {
                id: 'service',
                title: 'Service',
                description:
                  'Configure service case type settings, auto-assignment, and queue management',
                url: '/admin/organization/case-types/service',
                icon: Building,
                permissions: [SYSTEM_ADMIN, SERVICE_CASE_MANAGEMENT],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/CaseTypes/Service/service',
                hasRoute: true,
                children: [
                  {
                    id: 'service-auto-close',
                    title: 'Auto Close Cases',
                    description:
                      'Configure time period without inbound messages to automatically close the case.',
                    url: '/admin/organization/case-types/service/auto-close-cases',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['service'],
                    searchKeywords: [
                      'auto',
                      'close',
                      'automatic',
                      'inactivity',
                    ],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Service/AutoClose/auto-close',
                    hasRoute: true,
                  },
                  {
                    id: 'service-call-management',
                    title: 'Call management',
                    description:
                      'Configure how inbound calls to your vanity number are handled',
                    url: '/admin/organization/case-types/service/call-management',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN, SERVICE_CASE_MANAGEMENT],
                    tags: ['service'],
                    searchKeywords: [
                      'call',
                      'management',
                      'number',
                      'phone',
                      'vanity',
                      'voice',
                    ],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Service/CallManagement/call-management',
                    hasRoute: true,
                  },
                  {
                    id: 'service-welcome-messages',
                    title: 'Welcome Messages',
                    description:
                      'Configure inbound and outbound welcome messages for service cases',
                    url: '/admin/organization/case-types/service/welcome-messages',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN, SERVICE_CASE_MANAGEMENT],
                    tags: ['service', 'welcome'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessages/welcome-messages',
                    hasRoute: true,
                    children: [
                      {
                        id: 'service-welcome-outbound',
                        title: 'Outbound',
                        description:
                          'Configure outbound welcome messages for service cases',
                        url: '/admin/organization/case-types/service/welcome-messages/outbound',
                        icon: Building,
                        permissions: [SYSTEM_ADMIN, SERVICE_CASE_MANAGEMENT],
                        tags: ['service', 'welcome', 'outbound'],
                        searchKeywords: [],
                        componentPath:
                          '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessages/welcome-scenarios',
                        hasRoute: true,
                        children: [
                          {
                            id: 'service-welcome-outbound-not-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for service cases when not opted in',
                            url: '/admin/organization/case-types/service/welcome-messages/outbound/not-opted-in',
                            icon: Building,
                            permissions: [
                              SYSTEM_ADMIN,
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            tags: ['service', 'notOptedIn', 'outbound'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'service-welcome-outbound-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for service cases when opted in',
                            url: '/admin/organization/case-types/service/welcome-messages/outbound/opted-in',
                            icon: Building,
                            permissions: [
                              SYSTEM_ADMIN,
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            tags: ['service', 'optedIn', 'outbound'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessageOptedIn/welcome-message-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'service-welcome-outbound-marley-opt-in-disabled',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for service cases when Marley opt-in is disabled',
                            url: '/admin/organization/case-types/service/welcome-messages/outbound/marley-opt-in-disabled',
                            icon: Building,
                            permissions: [
                              SYSTEM_ADMIN,
                              SERVICE_CASE_MANAGEMENT,
                              AUTO_OPT_IN_USERS,
                            ],
                            tags: [
                              'service',
                              'marleyOptInDisabled',
                              'outbound',
                            ],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                            hasRoute: true,
                          },
                        ],
                      },
                      {
                        id: 'service-welcome-inbound',
                        title: 'Inbound',
                        description:
                          'Configure inbound welcome messages for service cases',
                        url: '/admin/organization/case-types/service/welcome-messages/inbound',
                        icon: Building,
                        permissions: [SYSTEM_ADMIN, SERVICE_CASE_MANAGEMENT],
                        tags: ['service', 'welcome', 'inbound'],
                        searchKeywords: [],
                        componentPath:
                          '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessages/welcome-scenarios',
                        hasRoute: true,
                        children: [
                          {
                            id: 'service-welcome-inbound-not-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for service cases when not opted in',
                            url: '/admin/organization/case-types/service/welcome-messages/inbound/not-opted-in',
                            icon: Building,
                            permissions: [
                              SYSTEM_ADMIN,
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            tags: ['service', 'notOptedIn', 'inbound'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'service-welcome-inbound-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for service cases when opted in',
                            url: '/admin/organization/case-types/service/welcome-messages/inbound/opted-in',
                            icon: Building,
                            permissions: [
                              SYSTEM_ADMIN,
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            tags: ['service', 'optedIn', 'inbound'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessageOptedIn/welcome-message-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'service-welcome-inbound-marley-opt-in-disabled',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for service cases when Marley opt-in is disabled',
                            url: '/admin/organization/case-types/service/welcome-messages/inbound/marley-opt-in-disabled',
                            icon: Building,
                            permissions: [
                              SYSTEM_ADMIN,
                              SERVICE_CASE_MANAGEMENT,
                              AUTO_OPT_IN_USERS,
                            ],
                            tags: ['service', 'marleyOptInDisabled', 'inbound'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                            hasRoute: true,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    id: 'service-welcome-message-not-opted-in',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for service cases when not opted in',
                    url: '/admin/organization/case-types/service/welcome-message-not-opted-in',
                    icon: Building,
                    permissions: [
                      SYSTEM_ADMIN,
                      notPermission(AUTO_OPT_IN_USERS),
                    ],
                    tags: ['service', 'notOptedIn'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                    hasRoute: true,
                  },
                  {
                    id: 'service-welcome-message-opted-in',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for service cases when opted in',
                    url: '/admin/organization/case-types/service/welcome-message-opted-in',
                    icon: Building,
                    permissions: [
                      SYSTEM_ADMIN,
                      notPermission(AUTO_OPT_IN_USERS),
                    ],
                    tags: ['service', 'optedIn'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessageOptedIn/welcome-message-opted-in',
                    hasRoute: true,
                  },
                  {
                    id: 'service-reassignment-message',
                    title: 'Reassignment message',
                    description:
                      'Configure reassignment message for service cases',
                    url: '/admin/organization/case-types/service/reassignment-message',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['service'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Service/ReassignmentMessage/reassignment-message',
                    hasRoute: true,
                  },
                  {
                    id: 'service-assignment-message',
                    title: 'Assignment message',
                    description:
                      'Configure assignment message for service cases',
                    url: '/admin/organization/case-types/service/assignment-message',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['service'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Service/AssignmentMessage/assignment-message',
                    hasRoute: true,
                  },
                  {
                    id: 'service-welcome-message-marley-opt-in-disabled',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for service cases when Marley opt-in is disabled',
                    url: '/admin/organization/case-types/service/welcome-message-marley-opt-in-disabled',
                    icon: Building,
                    permissions: [
                      SYSTEM_ADMIN,
                      SERVICE_CASE_MANAGEMENT,
                      AUTO_OPT_IN_USERS,
                    ],
                    searchKeywords: [],
                    tags: ['service', 'marleyOptInDisabled'],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Service/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                    hasRoute: true,
                  },
                ],
              },
              {
                id: 'policy',
                title: 'Policy',
                description: 'Configure policy case type settings',
                url: '/admin/organization/case-types/policy',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/CaseTypes/Policy/policy',
                hasRoute: true,
                children: [
                  {
                    id: 'policy-auto-close',
                    title: 'Auto Close Cases',
                    description:
                      'Configure time period without inbound messages to automatically close the case.',
                    url: '/admin/organization/case-types/policy/auto-close-cases',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['policy'],
                    searchKeywords: [
                      'auto',
                      'close',
                      'automatic',
                      'inactivity',
                    ],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Policy/AutoClose/auto-close',
                    hasRoute: true,
                  },
                  {
                    id: 'policy-assignment-message',
                    title: 'Assignment message',
                    description: 'Configure assignment message for policies',
                    url: '/admin/organization/case-types/policy/assignment-message',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['policy'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Policy/AssignmentMessage/assignment-message',
                    hasRoute: true,
                  },
                  {
                    id: 'policy-welcome-message-not-opted-in',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for policies when not opted in',
                    url: '/admin/organization/case-types/policy/welcome-message-not-opted-in',
                    icon: Building,
                    permissions: [
                      SYSTEM_ADMIN,
                      notPermission(AUTO_OPT_IN_USERS),
                    ],
                    searchKeywords: [],
                    tags: ['policy', 'notOptedIn'],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Policy/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                    hasRoute: true,
                  },
                  {
                    id: 'policy-welcome-message-opted-in',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for policies when opted in',
                    url: '/admin/organization/case-types/policy/welcome-message-opted-in',
                    icon: Building,
                    permissions: [
                      SYSTEM_ADMIN,
                      notPermission(AUTO_OPT_IN_USERS),
                    ],
                    tags: ['policy', 'optedIn'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Policy/WelcomeMessageOptedIn/welcome-message-opted-in',
                    hasRoute: true,
                  },
                  {
                    id: 'policy-welcome-message-marley-opt-in-disabled',
                    title: 'Welcome message',
                    description:
                      'Configure welcome message for policies when Marley opt-in is disabled',
                    url: '/admin/organization/case-types/policy/welcome-message-marley-opt-in-disabled',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN, AUTO_OPT_IN_USERS],
                    tags: ['policy', 'marleyOptInDisabled'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Policy/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                    hasRoute: true,
                  },
                  {
                    id: 'policy-reassignment-message',
                    title: 'Reassignment message',
                    description:
                      'Configure reassignment message for policy cases',
                    url: '/admin/organization/case-types/policy/reassignment-message',
                    icon: Building,
                    permissions: [SYSTEM_ADMIN],
                    tags: ['policy'],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/CaseTypes/Policy/ReassignmentMessage/reassignment-message',
                    hasRoute: true,
                  },
                ],
              },
            ],
          },
          {
            id: 'system-messages',
            title: 'System Messages',
            description: 'Configure system messages that apply to all cases.',
            url: '/admin/organization/system-messages',
            icon: Building,
            permissions: [SYSTEM_ADMIN],
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/Organization/SystemMessages/system-messages',
            hasRoute: true,
            children: [
              {
                id: 'mpt-messages',
                title: 'Multi-Party Texting Messages',
                description:
                  'Configure the automated messages related to multi-party texting.',
                url: '/admin/organization/system-messages/mpt-messages',
                icon: MessageSquare,
                permissions: [SYSTEM_ADMIN, MULTI_PARTY_TEXTING],
                searchKeywords: ['multiparty', 'multi-party', 'mpt'],
                componentPath:
                  '@app/components/Admin/Organization/SystemMessages/MultiPartyMessages/multi-party-messages',
                hasRoute: true,
                children: [
                  {
                    id: 'mpt-invite-not-opted-in',
                    title: 'Invitation Message',
                    description:
                      'Configure message inviting a contact that is not opted in.',
                    url: '/admin/organization/system-messages/mpt-messages/invite-not-opted-in',
                    icon: MessageSquare,
                    permissions: [SYSTEM_ADMIN, MULTI_PARTY_TEXTING],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/SystemMessages/MultiPartyMessages/InviteNotOptedIn/invite-not-opted-in',
                    hasRoute: true,
                    tags: ['notOptedIn'],
                  },
                  {
                    id: 'mpt-invite-opted-in',
                    title: 'Invitation Message',
                    description:
                      'Configure message inviting a contact that is already opted in.',
                    url: '/admin/organization/system-messages/mpt-messages/invite-opted-in',
                    icon: MessageSquare,
                    permissions: [SYSTEM_ADMIN, MULTI_PARTY_TEXTING],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/SystemMessages/MultiPartyMessages/InviteOptedIn/invite-opted-in',
                    hasRoute: true,
                    tags: ['optedIn'],
                  },
                  {
                    id: 'mpt-contact-joined',
                    title: 'Contact Joined Message',
                    description:
                      'Configure message to notify all contacts when someone has joined the conversation.',
                    url: '/admin/organization/system-messages/mpt-messages/contact-joined',
                    icon: MessageSquare,
                    permissions: [SYSTEM_ADMIN, MULTI_PARTY_TEXTING],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/SystemMessages/MultiPartyMessages/ContactJoined/contact-joined',
                    hasRoute: true,
                  },
                  {
                    id: 'mpt-contact-removed',
                    title: 'Contact Removed Message',
                    description:
                      'Configure message to notify all contacts when someone has left the conversation.',
                    url: '/admin/organization/system-messages/mpt-messages/contact-removed',
                    icon: MessageSquare,
                    permissions: [SYSTEM_ADMIN, MULTI_PARTY_TEXTING],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/SystemMessages/MultiPartyMessages/ContactRemoved/contact-removed',
                    hasRoute: true,
                  },
                  {
                    id: 'mpt-conversation-closed',
                    title: 'Conversation Closed Message',
                    description:
                      'Configure message to notify a removed contact that they are no longer a part of the conversation.',
                    url: '/admin/organization/system-messages/mpt-messages/conversation-closed',
                    icon: MessageSquare,
                    permissions: [SYSTEM_ADMIN, MULTI_PARTY_TEXTING],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Organization/SystemMessages/MultiPartyMessages/ConversationClosed/conversation-closed',
                    hasRoute: true,
                  },
                ],
              },
              {
                id: 'locked-out-message',
                title: 'Locked Out Message',
                description:
                  'Configure the automated message when a contact messages while locked out.',
                url: '/admin/organization/system-messages/locked-out-message',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/SystemMessages/LockedOutMessage/locked-out-message',
                hasRoute: true,
              },
              {
                id: 'invalid-opt-in-response',
                title: 'Invalid Opt-In Response',
                description:
                  'Configure the automated message when a contact does not opt-in correctly.',
                url: '/admin/organization/system-messages/invalid-opt-in-response',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/SystemMessages/InvalidOptInResponse/invalid-opt-in-response',
                hasRoute: true,
              },
              {
                id: 'out-of-office-messages',
                title: 'Out of Office Messages',
                description:
                  'Configure Out of Office message behavior for secondary assignees',
                url: '/admin/organization/system-messages/out-of-office-messages',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/OutOfOfficeMessages/out-of-office-messages',
                hasRoute: true,
              },
            ],
          },
          {
            id: 'active-hours',
            title: 'Active hours',
            description:
              'Define the days, times, and timezone when your organization is available for messaging.',
            url: '/admin/organization/active-hours',
            icon: Building,
            permissions: [SYSTEM_ADMIN],
            searchKeywords: [
              'active hours',
              'business hours',
              'schedule',
              'quiet hours',
            ],
            componentPath:
              '@app/components/Admin/Organization/ActiveHours/active-hours',
            hasRoute: true,
          },
          {
            id: 'features',
            title: 'Features',
            description:
              'Manage feature configuration within your organization. Some features may require Hi Marley assistance to enable.',
            url: '/admin/organization/features',
            icon: Building,
            permissions: [SYSTEM_ADMIN],
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/Organization/Features/features',
            hasRoute: true,
            children: [
              {
                id: 'audit-log',
                title: 'Audit Log',
                description: 'View feature configuration change history',
                url: '/admin/organization/features/audit-log',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/Features/AuditLog/audit-log',
                hasRoute: true,
              },
              {
                id: 'visual-voicemail',
                title: 'Visual Voicemail',
                description: 'Manage features for Visual Voicemail',
                url: '/admin/organization/features/visual-voicemail',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/Features/VisualVoicemail/visual-voicemail',
                hasRoute: true,
              },
              {
                id: 'coaching',
                title: 'Coaching',
                description: 'Manage features for Coaching',
                url: '/admin/organization/features/coaching',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/Features/Coaching/coaching',
                hasRoute: true,
              },
              {
                id: 'notes-access',
                title: 'Notes',
                description: 'Manage features for Notes',
                url: '/admin/organization/features/notes-access',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: ['notes', 'note replies', 'collaboration'],
                componentPath:
                  '@app/components/Admin/Organization/Features/Notes/notes-access',
                hasRoute: true,
              },
              {
                id: 'auto-resend-welcome-message',
                title: 'Auto Resend Welcome Message',
                description:
                  'Configure automatic resending of welcome messages',
                url: '/admin/organization/features/auto-resend-welcome-message',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/Features/AutoResendWelcomeMessage/auto-resend-welcome-message',
                hasRoute: true,
              },
              {
                id: 'features-quiet-hours',
                title: 'Quiet Hours',
                description:
                  'Set the active hours when messages can send. Anything outside is queued until the next active window.',
                url: '/admin/organization/features/quiet-hours',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [
                  'quiet hours',
                  'active hours',
                  'business hours',
                  'schedule',
                ],
                componentPath:
                  '@app/components/Admin/Organization/Features/QuietHours/quiet-hours',
                hasRoute: true,
              },
              {
                id: 'org-inactivity-auto-reply',
                title: 'Org Inactivity Auto Reply',
                description:
                  'Add an auto reply text for received messages on cases when users are inactive for a specified time.',
                url: '/admin/organization/features/org-inactivity-auto-reply',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Organization/Features/OrgInactivityAutoReply/org-inactivity-auto-reply',
                hasRoute: true,
              },
            ],
          },
          {
            id: 'internal-features',
            title: 'Internal Features',
            description:
              'Manage internal-only feature configuration within this organization. Some features may require Hi Marley assistance to enable.',
            url: '/admin/organization/internal-features',
            icon: Building,
            permissions: [SYSTEM_ADMIN],
            searchKeywords: ['internal', 'features', 'oauth', 'diagnostic'],
            componentPath:
              '@app/components/Admin/Organization/Features/internal-features',
            hasRoute: true,
          },
          {
            id: 'inbox',
            title: 'Inbox',
            description:
              'Configure default inbox behavior for users in your organization.',
            url: '/admin/organization/inbox',
            icon: Inbox,
            permissions: [SYSTEM_ADMIN],
            searchKeywords: [
              'inbox',
              'notifications',
              'unread',
              'unaddressed',
              'internal',
              'message',
            ],
            componentPath:
              '@app/components/Admin/Organization/InboxNotifications/inbox-notifications',
            hasRoute: true,
          },
          {
            id: 'media-restrictions',
            title: 'Media Restrictions',
            description:
              'Configure which media types and file formats are allowed for messaging.',
            url: '/admin/organization/media-restrictions',
            icon: Building,
            permissions: [SYSTEM_ADMIN],
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/Organization/MediaRestrictions/media-restrictions',
            hasRoute: true,
          },
          {
            id: 'security',
            title: 'Security',
            description:
              'Configure security settings including mTLS authentication and OAuth 2.0.',
            url: '/admin/organization/security',
            icon: Building,
            permissions: [SYSTEM_ADMIN],
            searchKeywords: [
              'security',
              'mtls',
              'tls',
              'certificates',
              'oauth',
              'api',
              'authentication',
            ],
            componentPath:
              '@app/components/Admin/Organization/Security/security',
            hasRoute: true,
            children: [
              {
                id: 'mtls',
                title: 'mTLS',
                description:
                  'Manage mutual TLS authentication and client certificates for secure API access.',
                url: '/admin/organization/security/mTLS',
                icon: Building,
                permissions: [SYSTEM_ADMIN, MTLS_CONFIG_UI],
                searchKeywords: [
                  'mutual tls',
                  'client certificates',
                  'ssl',
                  'authentication',
                  'certificate',
                ],
                componentPath:
                  '@app/components/Admin/Organization/Security/mTLS/mtls',
                hasRoute: true,
              },
              {
                id: 'oauth-credentials',
                title: 'OAuth Credentials',
                description: 'Manage OAuth 2.0 credentials and client secrets',
                url: '/admin/organization/security/oauth-credentials',
                icon: Key,
                permissions: [SYSTEM_ADMIN, OAUTH_2_UI],
                searchKeywords: [],
                tags: ['oauth', 'api', 'authentication'],
                componentPath:
                  '@app/components/Admin/Organization/Security/OAuth2/oauth-credentials',
                hasRoute: true,
                children: [
                  {
                    id: 'oauth-secrets',
                    title: 'OAuth Secrets',
                    description: 'Manage secrets for an OAuth 2.0 client',
                    url: '/admin/organization/security/oauth-credentials/oauth-secrets',
                    permissions: [SYSTEM_ADMIN, OAUTH_2_UI],
                    componentPath:
                      '@app/components/Admin/Organization/Security/OAuth2/oauth-secrets',
                    hasRoute: true,
                    icon: Key,
                  },
                ],
              },
              {
                id: 'SSO',
                title: 'Single Sign-On (SSO)',
                description:
                  'Integration with your Identity Provider (Hi Marley assistance required).',
                url: '/admin/organization/security/SSO',
                icon: Building,
                permissions: [SYSTEM_ADMIN],
                searchKeywords: [
                  'sso',
                  'single sign-on',
                  'identity provider',
                  'okta',
                  'saml',
                ],
                componentPath: '@app/components/Admin/Organization/SSO/sso',
                hasRoute: true,
              },
            ],
          },
          {
            id: 'language-preferences',
            title: 'Language',
            description:
              'Set the language Hi Marley is displayed in and messages are sent in for your organization.',
            url: '/admin/organization/language-preferences',
            icon: Building,
            permissions: [SYSTEM_ADMIN, LOCALIZATION_SETTINGS],
            searchKeywords: ['language', 'locale', 'french', 'english'],
            componentPath:
              '@app/components/Admin/Organization/Language/language',
            hasRoute: true,
          },
          {
            id: 'config-exports',
            title: 'Config Exports',
            description:
              'Export configuration reports as CSV files for features, permissions, and rulesets.',
            url: '/admin/organization/config-exports',
            icon: FileText,
            // TODO: Add permission IS_ALIAS_OPERATOR once this PR is merged: https://github.com/himarley/marley-chat/pull/7332
            // and ensure the isAliasOperator check is removed from the component.
            permissions: [SYSTEM_ADMIN],
            searchKeywords: [
              'export',
              'csv',
              'download',
              'config',
              'features',
              'permissions',
              'rulesets',
            ],
            componentPath:
              '@app/components/Admin/Organization/ConfigExports/config-exports',
            hasRoute: true,
          },
        ],
      },
      {
        id: 'operational',
        title: 'Operational',
        description:
          'Operational settings including brands, lines of business, and message templates',
        url: '/admin/operational',
        icon: Building2,
        permissions: [anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB)],
        searchKeywords: [],
        componentPath: '@app/components/Admin/Operational/operational',
        hasRoute: true,
        children: [
          {
            id: 'brands',
            title: 'Brands',
            description: 'Configure brands and lines of business (LOBs)',
            url: '/admin/operational/brands',
            icon: Building2,
            permissions: [anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB)],
            disallowedRoles: [SUPERVISOR],
            searchKeywords: [],
            componentPath: '@app/components/Admin/Operational/Brands/brands',
            hasRoute: true,
            children: [
              {
                id: 'brand',
                title: 'Brand',
                description: 'Configure brands',
                url: '/admin/operational/brands/:brandName',
                icon: Building2,
                permissions: [anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB)],
                disallowedRoles: [SUPERVISOR],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Operational/Brands/Brand/brand',
                hasRoute: true,
                children: [
                  {
                    id: 'line-of-business',
                    title: 'Line of Business',
                    description: 'Configure line of business',
                    url: '/admin/operational/brands/:brandName/:lobKey',
                    icon: Building2,
                    permissions: [anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB)],
                    disallowedRoles: [SUPERVISOR],
                    searchKeywords: [],
                    componentPath:
                      '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/line-of-business',
                    hasRoute: true,
                    children: [
                      {
                        id: 'lob-claim',
                        title: 'Claim',
                        description:
                          'Configure claim case type settings for this line of business',
                        url: '/admin/operational/brands/:brandName/:lobKey/claim',
                        icon: Building2,
                        permissions: [anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB)],
                        disallowedRoles: [SUPERVISOR],
                        searchKeywords: [],
                        componentPath:
                          '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Claim/claim',
                        hasRoute: true,
                        children: [
                          {
                            id: 'lob-claim-auto-close',
                            title: 'Auto Close Cases',
                            description:
                              'Configure time period without inbound messages to automatically close the case.',
                            url: '/admin/operational/brands/:brandName/:lobKey/claim/auto-close-cases',
                            icon: Building2,
                            permissions: [SYSTEM_ADMIN],
                            tags: ['claim'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Claim/AutoClose/auto-close',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-claim-welcome-message-not-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for claims when not opted in',
                            url: '/admin/operational/brands/:brandName/:lobKey/claim/welcome-message-not-opted-in',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            tags: ['claim', 'notOptedIn'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Claim/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-claim-welcome-message-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for claims when opted in',
                            url: '/admin/operational/brands/:brandName/:lobKey/claim/welcome-message-opted-in',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            disallowedRoles: [SUPERVISOR],
                            searchKeywords: [],
                            tags: ['claim', 'optedIn'],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Claim/WelcomeMessageOptedIn/welcome-message-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-claim-welcome-message-marley-opt-in-disabled',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for claims when Marley opt-in is disabled',
                            url: '/admin/operational/brands/:brandName/:lobKey/claim/welcome-message-marley-opt-in-disabled',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              AUTO_OPT_IN_USERS,
                            ],
                            disallowedRoles: [SUPERVISOR],
                            tags: ['claim', 'marleyOptInDisabled'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Claim/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-claim-reassignment-message',
                            title: 'Reassignment message',
                            description:
                              'Configure reassignment message for claims in this line of business',
                            url: '/admin/operational/brands/:brandName/:lobKey/claim/reassignment-message',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                            ],
                            disallowedRoles: [SUPERVISOR],
                            tags: ['claim'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Claim/ReassignmentMessage/reassignment-message',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-claim-assignment-message',
                            title: 'Assignment message',
                            description:
                              'Configure assignment message for claims in this line of business',
                            url: '/admin/operational/brands/:brandName/:lobKey/claim/assignment-message',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                            ],
                            disallowedRoles: [SUPERVISOR],
                            tags: ['claim'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Claim/AssignmentMessage/assignment-message',
                            hasRoute: true,
                          },
                        ],
                      },
                      {
                        id: 'lob-general',
                        title: 'General',
                        description:
                          'Configure general case type settings for this line of business',
                        url: '/admin/operational/brands/:brandName/:lobKey/general',
                        icon: Building2,
                        permissions: [SYSTEM_ADMIN],
                        disallowedRoles: [SUPERVISOR],
                        searchKeywords: [],
                        componentPath:
                          '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/General/general',
                        hasRoute: true,
                        children: [
                          {
                            id: 'lob-general-welcome-message-not-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for general cases when not opted in',
                            url: '/admin/operational/brands/:brandName/:lobKey/general/welcome-message-not-opted-in',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            tags: ['general', 'notOptedIn'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/General/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-general-welcome-message-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for general cases when opted in',
                            url: '/admin/operational/brands/:brandName/:lobKey/general/welcome-message-opted-in',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            disallowedRoles: [SUPERVISOR],
                            searchKeywords: [],
                            tags: ['general', 'optedIn'],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/General/WelcomeMessageOptedIn/welcome-message-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-general-welcome-message-marley-opt-in-disabled',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for general cases when Marley opt-in is disabled',
                            url: '/admin/operational/brands/:brandName/:lobKey/general/welcome-message-marley-opt-in-disabled',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              AUTO_OPT_IN_USERS,
                            ],
                            disallowedRoles: [SUPERVISOR],
                            tags: ['general', 'marleyOptInDisabled'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/General/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-general-reassignment-message',
                            title: 'Reassignment message',
                            description:
                              'Configure reassignment message for general cases',
                            url: '/admin/operational/brands/:brandName/:lobKey/general/reassignment-message',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                            ],
                            disallowedRoles: [SUPERVISOR],
                            tags: ['general'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/General/ReassignmentMessage/reassignment-message',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-general-auto-close',
                            title: 'Auto Close Cases',
                            description:
                              'Configure time period without inbound messages to automatically close the case.',
                            url: '/admin/operational/brands/:brandName/:lobKey/general/auto-close-cases',
                            icon: Building2,
                            permissions: [SYSTEM_ADMIN],
                            tags: ['general'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/General/AutoClose/auto-close',
                            hasRoute: true,
                          },
                        ],
                      },
                      {
                        id: 'lob-policy',
                        title: 'Policy',
                        description:
                          'Configure policy case type settings for this line of business',
                        url: '/admin/operational/brands/:brandName/:lobKey/policy',
                        icon: Building2,
                        permissions: [SYSTEM_ADMIN],
                        disallowedRoles: [SUPERVISOR],
                        searchKeywords: [],
                        componentPath:
                          '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Policy/policy',
                        hasRoute: true,
                        children: [
                          {
                            id: 'lob-policy-auto-close',
                            title: 'Auto Close Cases',
                            description:
                              'Configure time period without inbound messages to automatically close the case.',
                            url: '/admin/operational/brands/:brandName/:lobKey/policy/auto-close-cases',
                            icon: Building2,
                            permissions: [SYSTEM_ADMIN],
                            tags: ['policy'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Policy/AutoClose/auto-close',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-policy-welcome-message-not-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for policies when not opted in',
                            url: '/admin/operational/brands/:brandName/:lobKey/policy/welcome-message-not-opted-in',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            tags: ['policy', 'notOptedIn'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Policy/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-policy-welcome-message-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for policies when opted in',
                            url: '/admin/operational/brands/:brandName/:lobKey/policy/welcome-message-opted-in',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            disallowedRoles: [SUPERVISOR],
                            searchKeywords: [],
                            tags: ['policy', 'optedIn'],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Policy/WelcomeMessageOptedIn/welcome-message-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-policy-welcome-message-marley-opt-in-disabled',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for policies when Marley opt-in is disabled',
                            url: '/admin/operational/brands/:brandName/:lobKey/policy/welcome-message-marley-opt-in-disabled',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              AUTO_OPT_IN_USERS,
                            ],
                            disallowedRoles: [SUPERVISOR],
                            tags: ['policy', 'marleyOptInDisabled'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Policy/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-policy-reassignment-message',
                            title: 'Reassignment message',
                            description:
                              'Configure reassignment message for policies in this line of business',
                            url: '/admin/operational/brands/:brandName/:lobKey/policy/reassignment-message',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                            ],
                            disallowedRoles: [SUPERVISOR],
                            tags: ['policy'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Policy/ReassignmentMessage/reassignment-message',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-policy-assignment-message',
                            title: 'Assignment message',
                            description:
                              'Configure assignment message for policies in this line of business',
                            url: '/admin/operational/brands/:brandName/:lobKey/policy/assignment-message',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                            ],
                            disallowedRoles: [SUPERVISOR],
                            tags: ['policy'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Policy/AssignmentMessage/assignment-message',
                            hasRoute: true,
                          },
                        ],
                      },
                      {
                        id: 'lob-service',
                        title: 'Service',
                        description:
                          'Configure service case type settings for this line of business',
                        url: '/admin/operational/brands/:brandName/:lobKey/service',
                        icon: Building2,
                        permissions: [
                          anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                          SERVICE_CASE_MANAGEMENT,
                        ],
                        disallowedRoles: [SUPERVISOR],
                        searchKeywords: [],
                        componentPath:
                          '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Service/service',
                        hasRoute: true,
                        children: [
                          {
                            id: 'lob-service-auto-close',
                            title: 'Auto Close Cases',
                            description:
                              'Configure time period without inbound messages to automatically close the case.',
                            url: '/admin/operational/brands/:brandName/:lobKey/service/auto-close-cases',
                            icon: Building2,
                            permissions: [SYSTEM_ADMIN],
                            tags: ['service'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Service/AutoClose/auto-close',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-service-welcome-message-not-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for service cases when not opted in',
                            url: '/admin/operational/brands/:brandName/:lobKey/service/welcome-message-not-opted-in',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              SERVICE_CASE_MANAGEMENT,
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            disallowedRoles: [SUPERVISOR],
                            searchKeywords: [],
                            tags: ['service', 'notOptedIn'],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Service/WelcomeMessageNotOptedIn/welcome-message-not-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-service-welcome-message-opted-in',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for service cases when opted in',
                            url: '/admin/operational/brands/:brandName/:lobKey/service/welcome-message-opted-in',
                            icon: Building,
                            disallowedRoles: [SUPERVISOR],
                            tags: ['service', 'optedIn'],
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              SERVICE_CASE_MANAGEMENT,
                              notPermission(AUTO_OPT_IN_USERS),
                            ],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Service/WelcomeMessageOptedIn/welcome-message-opted-in',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-service-welcome-message-marley-opt-in-disabled',
                            title: 'Welcome message',
                            description:
                              'Configure welcome message for service cases when Marley opt-in is disabled',
                            url: '/admin/operational/brands/:brandName/:lobKey/service/welcome-message-marley-opt-in-disabled',
                            icon: Building,
                            disallowedRoles: [SUPERVISOR],
                            tags: ['service', 'marleyOptInDisabled'],
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              SERVICE_CASE_MANAGEMENT,
                              AUTO_OPT_IN_USERS,
                            ],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Service/WelcomeMessageMarleyOptInDisabled/welcome-message-marley-opt-in-disabled',
                            hasRoute: true,
                          },
                          {
                            id: 'lob-service-assignment-message',
                            title: 'Assignment message',
                            description:
                              'Configure assignment message for service cases in this line of business',
                            url: '/admin/operational/brands/:brandName/:lobKey/service/assignment-message',
                            icon: Building2,
                            permissions: [
                              anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB),
                              SERVICE_CASE_MANAGEMENT,
                            ],
                            disallowedRoles: [SUPERVISOR],
                            tags: ['service'],
                            searchKeywords: [],
                            componentPath:
                              '@app/components/Admin/Operational/Brands/Brand/LineOfBusiness/Service/AssignmentMessage/assignment-message',
                            hasRoute: true,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            id: 'message-templates',
            title: 'Message Templates',
            description: 'Manage message templates',
            url: '/admin/operational/message-templates',
            icon: MessageSquare,
            permissions: [anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB)],
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/Operational/MessageTemplates/message-templates',
            hasRoute: true,
          },
        ],
      },
      {
        id: 'notifications',
        title: 'Notifications',
        description: 'Notification settings and preferences',
        url: '/admin/notifications',
        icon: BellDot,
        permissions: [SYSTEM_ADMIN, ADMIN_NOTIFICATION_CONFIG],
        searchKeywords: [],
        componentPath: '@app/components/Admin/Notifications/notifications',
        hasRoute: true,
      },
      {
        id: 'event-logs',
        title: 'Event Logs',
        description:
          'View organization configuration and settings change history',
        url: '/admin/event-logs',
        icon: FileText,
        permissions: [SYSTEM_ADMIN],
        searchKeywords: ['event', 'log', 'history', 'audit', 'changes'],
        componentPath: '@app/components/Admin/EventLogs/event-logs',
        hasRoute: true,
      },
    ],
  },
  {
    id: 'team-management-section',
    title: 'Team management',
    description: 'User, group, and team management settings',
    items: [
      {
        id: 'team-management',
        title: 'Team management',
        description: 'Manage users, groups and team permissions',
        getDescription: (ctx) =>
          ctx.isQueuesEnabled
            ? (ctx.t?.('admin:team-management.descriptionWithQueues', {
                defaultValue:
                  'Manage users, groups, queues, and team permissions',
              }) ?? 'Manage users, groups, queues, and team permissions')
            : undefined,
        url: '/admin/team-management',
        icon: Users2,
        permissions: [USER_MANAGEMENT_TAB],
        searchKeywords: [],
        componentPath: '@app/components/Admin/TeamManagement/team-management',
        children: [
          {
            id: 'team-management-users',
            title: 'Users',
            description: 'Manage user accounts',
            url: '/admin/team-management/users',
            icon: Users2,
            permissions: [USER_MANAGEMENT_TAB, USER_MANAGEMENT],
            searchKeywords: [],
            componentPath: '@app/components/Admin/TeamManagement/Users/users',
            hasRoute: true,
          },
          {
            id: 'team-management-groups',
            title: 'Groups',
            description: 'Manage user groups',
            url: '/admin/team-management/groups',
            icon: Users2,
            permissions: [USER_MANAGEMENT_TAB, GROUP_MANAGEMENT],
            searchKeywords: [],
            componentPath: '@app/components/Admin/TeamManagement/Groups/groups',
            hasRoute: true,
            children: [
              {
                id: 'team-management-groups-detail',
                title: 'Group Detail',
                description: 'View and manage group details',
                url: '/admin/team-management/groups/:id',
                icon: Users2,
                permissions: [USER_MANAGEMENT_TAB, GROUP_MANAGEMENT],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/TeamManagement/Groups/groups',
                hasRoute: true,
              },
            ],
          },
          {
            id: 'team-management-queues',
            title: 'Queues',
            description: 'Manage queues and collaboration settings',
            url: '/admin/team-management/queues',
            icon: MessageSquare,
            permissions: [QUEUES, QUEUE_MANAGEMENT],
            searchKeywords: [],
            componentPath: '@app/components/Admin/TeamManagement/Queues/queues',
            hasRoute: true,
            children: [
              {
                id: 'team-management-queues-detail',
                title: 'Queue Detail',
                description: 'View and manage queue details',
                url: '/admin/team-management/queues/:id',
                icon: MessageSquare,
                permissions: [QUEUES, QUEUE_MANAGEMENT],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/TeamManagement/Queues/queue-detail',
                hasRoute: true,
              },
              {
                id: 'team-management-queues-details-edit',
                title: 'Queue Details Edit',
                description: 'Edit queue name and unique ID',
                url: '/admin/team-management/queues/:id/details',
                icon: MessageSquare,
                permissions: [QUEUES, QUEUE_MANAGEMENT],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/TeamManagement/Queues/queue-details-edit',
                hasRoute: true,
              },
              {
                id: 'team-management-queues-groups',
                title: 'Queue Groups',
                description:
                  'Configure which groups are assigned to this queue',
                url: '/admin/team-management/queues/:id/groups',
                icon: MessageSquare,
                permissions: [QUEUES, QUEUE_MANAGEMENT],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/TeamManagement/Queues/queue-groups',
                hasRoute: true,
              },
              {
                id: 'team-management-queues-assignment',
                title: 'Case Routing',
                description: 'Configure case routing settings for this queue',
                url: '/admin/team-management/queues/:id/case-routing',
                icon: MessageSquare,
                permissions: [QUEUES, QUEUE_MANAGEMENT],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/TeamManagement/Queues/queue-assignment-edit',
                hasRoute: true,
              },
              {
                id: 'team-management-queues-auto-replies',
                title: 'Queue Auto Replies',
                description: 'Configure auto reply settings for this queue',
                url: '/admin/team-management/queues/:id/auto-replies',
                icon: MessageSquare,
                permissions: [QUEUES, QUEUE_MANAGEMENT],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/TeamManagement/Queues/queue-auto-replies',
                hasRoute: true,
              },
              {
                id: 'team-management-queues-auto-assign',
                title: 'Queue Auto Assign',
                description: 'Configure auto assign settings for this queue',
                url: '/admin/team-management/queues/:id/auto-assign',
                icon: MessageSquare,
                permissions: [QUEUES, QUEUE_MANAGEMENT],
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/TeamManagement/Queues/queue-auto-assign',
                hasRoute: true,
              },
            ],
          },
        ],
        hasRoute: true,
      },
    ],
  },
  {
    id: 'personal',
    title: 'Personal',
    description: 'Personal settings',
    items: [
      {
        id: 'personal-settings',
        title: 'Personal',
        description: 'Manage your profile, auto replies, and notifications',
        url: '/admin/personal',
        icon: User,
        searchKeywords: [],
        componentPath: '@app/components/Admin/Personal/personal',
        hasRoute: true,
        children: [
          {
            id: 'personal-profile',
            title: 'Profile',
            description: 'Manage your contact information',
            url: '/admin/personal/profile',
            icon: User,
            componentPath: '@app/components/Admin/Personal/Profile/profile',
            hasRoute: true,
          },
          {
            id: 'personal-auto-replies',
            title: 'Auto Replies',
            description: 'Configure auto replies for your profile.',
            url: '/admin/personal/auto-replies',
            icon: MessageSquare,
            componentPath:
              '@app/components/Admin/Personal/AutoReplies/auto-replies',
            hasRoute: true,
            children: [
              {
                id: 'auto-replies-out-of-office',
                title: 'Out of Office',
                description:
                  'Configure an automated reply that will be sent to contacts when you are away',
                url: '/admin/personal/auto-replies/out-of-office',
                icon: MessageSquare,
                componentPath:
                  '@app/components/Admin/Personal/AutoReplies/OutOfOffice/out-of-office',
                hasRoute: true,
              },
              {
                id: 'auto-replies-office-hours',
                title: 'Office Hours',
                description:
                  'Configure auto replies that will be sent to customers during regular business hours or after regular business hours.',
                url: '/admin/personal/auto-replies/office-hours',
                icon: MessageSquare,
                componentPath:
                  '@app/components/Admin/Personal/AutoReplies/OfficeHours/office-hours',
                hasRoute: true,
              },
            ],
          },
          {
            id: 'personal-notifications',
            title: 'Notifications',
            description: 'Configure notifications for your profile',
            url: '/admin/personal/notifications',
            icon: Bell,
            componentPath:
              '@app/components/Admin/Personal/Notifications/notifications',
            hasRoute: true,
          },
          {
            id: 'personal-customer-feedback',
            title: 'Customer Feedback',
            description: 'Results of your customer feedback surveys',
            url: '/admin/personal/customer-feedback',
            icon: Star,
            componentPath:
              '@app/components/Admin/Personal/CustomerFeedback/customer-feedback',
            hasRoute: true,
          },
          {
            id: 'personal-inbox-behavior',
            title: 'Inbox Notifications',
            description: 'Configure how cases are flagged for notification',
            url: '/admin/personal/inbox-behavior',
            icon: Bell,
            searchKeywords: [
              'inbox',
              'notifications',
              'unread',
              'unaddressed',
              'internal',
              'message',
            ],
            componentPath:
              '@app/components/Admin/Personal/InboxBehavior/inbox-behavior',
            hasRoute: true,
          },
          {
            id: 'personal-preferences',
            title: 'Preferences',
            description:
              'Set the language Hi Marley is displayed in and your messages are sent in.',
            url: '/admin/personal/preferences',
            icon: Globe,
            permissions: [LOCALIZATION_SETTINGS],
            searchKeywords: [
              'language',
              'locale',
              'preferences',
              'french',
              'english',
            ],
            componentPath:
              '@app/components/Admin/Personal/Preferences/preferences',
            hasRoute: true,
          },
          {
            id: 'personal-change-password',
            title: 'Change Password',
            description: 'Update your account password',
            url: '/admin/personal/change-password',
            icon: Lock,
            componentPath:
              '@app/components/Admin/Personal/ChangePassword/change-password',
            hasRoute: true,
          },
        ],
      },
    ],
  },
  {
    id: 'integrations-section',
    title: 'Integrations & services',
    description: 'Third-party integrations and external services',
    items: [
      {
        id: 'integrations',
        title: 'Integrations',
        description: 'Configure third-party integrations and API connections',
        url: '/admin/integrations',
        permissions: [anyOf(SYSTEM_ADMIN, USER_MANAGEMENT_TAB)],
        icon: Blocks,
        searchKeywords: [],
        componentPath: '@app/components/Admin/Integrations/integrations',
        loader: integrationPartnersLoader,
        children: [
          ...(isWorkflowManagementUIEnabled()
            ? [
                {
                  id: 'all-partner-workflows',
                  title: 'Workflows',
                  description: 'Manage workflows across all partners',
                  url: '/admin/integrations/workflows',
                  permissions: [SYSTEM_ADMIN],
                  icon: Blocks,
                  searchKeywords: [],
                  componentPath:
                    '@app/components/Admin/Integrations/Workflows/workflows',
                  hasRoute: true,
                  loader: integrationPartnersLoader,
                },
              ]
            : []),
          {
            id: 'partner-overview',
            title: 'Partner Overview',
            description: 'Configure integration settings',
            url: '/admin/integrations/:partnerName',
            permissions: [anyOf(PARTNER_CONFIG_VIEWER, SYSTEM_ADMIN)],
            icon: Blocks,
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/Integrations/PartnerOverview/partner-overview',
            hasRoute: true,
            loader: partnerConfigLoader,
            children: [
              {
                id: 'partner-enablement',
                title: 'Enablement',
                description: 'Configure integration enablement',
                url: '/admin/integrations/:partnerName/enablement',
                permissions: [PARTNER_CONFIG_VIEWER],
                icon: Blocks,
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Integrations/ConfigPages/EnablementPage/enablement-page',
                hasRoute: true,
              },
              {
                id: 'partner-regex',
                title: 'Regex',
                description: 'Configure claim regex pattern',
                url: '/admin/integrations/:partnerName/regex',
                permissions: [PARTNER_CONFIG_VIEWER],
                icon: Blocks,
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Integrations/ConfigPages/RegexPage/regex-page',
                hasRoute: true,
              },
              {
                id: 'partner-datasets',
                title: 'Datasets',
                description: 'Configure datasets',
                url: '/admin/integrations/:partnerName/datasets',
                permissions: [PARTNER_CONFIG_VIEWER],
                icon: Blocks,
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Integrations/ConfigPages/DatasetsPage/datasets-page',
                hasRoute: true,
              },
              {
                id: 'partner-seller-codes',
                title: 'Seller Codes',
                description: 'Configure seller code mappings',
                url: '/admin/integrations/:partnerName/seller-codes',
                permissions: [PARTNER_CONFIG_VIEWER],
                icon: Blocks,
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Integrations/ConfigPages/SellerCodesPage/seller-codes-page',
                hasRoute: true,
                loader: partnerConfigLoader,
              },
              {
                id: 'partner-mapping',
                title: 'Mapping',
                description: 'Configure integration mapping ID',
                url: '/admin/integrations/:partnerName/mapping',
                permissions: [PARTNER_CONFIG_VIEWER],
                icon: Blocks,
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Integrations/ConfigPages/MappingPage/mapping-page',
                hasRoute: true,
              },
              {
                id: 'partner-media-sync',
                title: 'Media Sync',
                description: 'Configure media sync settings',
                url: '/admin/integrations/:partnerName/media-sync',
                permissions: [PARTNER_CONFIG_VIEWER],
                icon: Blocks,
                searchKeywords: [],
                componentPath:
                  '@app/components/Admin/Integrations/ConfigPages/MediaSyncPage/media-sync-page',
                hasRoute: true,
              },
              ...(isWorkflowManagementUIEnabled()
                ? [
                    {
                      id: 'partner-workflows',
                      title: 'Workflows',
                      description: 'Configure workflow rules and settings',
                      url: '/admin/integrations/:partnerName/workflows',
                      permissions: [SYSTEM_ADMIN],
                      icon: Blocks,
                      searchKeywords: [],
                      componentPath:
                        '@app/components/Admin/Integrations/Workflows/workflows',
                      hasRoute: true,
                      loader: partnerConfigLoader,
                    },
                    {
                      id: 'partner-workflow-events',
                      title: 'Workflow Events',
                      description: 'Configure the events in a workflow',
                      url: '/admin/integrations/:partnerName/workflows/:workflowId',
                      permissions: [SYSTEM_ADMIN],
                      icon: Blocks,
                      searchKeywords: [],
                      componentPath:
                        '@app/components/Admin/Integrations/Workflows/WorkflowEvents/workflow-events',
                      hasRoute: true,
                      loader: partnerConfigLoader,
                    },
                    {
                      id: 'partner-workflow-event',
                      title: 'Edit Event',
                      description: 'Configure the activities on an event',
                      url: '/admin/integrations/:partnerName/workflows/:workflowId/:eventKey',
                      permissions: [SYSTEM_ADMIN],
                      icon: Blocks,
                      searchKeywords: [],
                      componentPath:
                        '@app/components/Admin/Integrations/Workflows/EditEvent/edit-event',
                      hasRoute: true,
                      loader: partnerConfigLoader,
                    },
                  ]
                : [
                    {
                      id: 'partner-rules',
                      title: 'Rules',
                      description: 'Configure rules and message templates',
                      url: '/admin/integrations/:partnerName/rules',
                      permissions: [anyOf(PARTNER_CONFIG_VIEWER, SYSTEM_ADMIN)],
                      icon: Blocks,
                      searchKeywords: [],
                      componentPath:
                        '@app/components/Admin/Integrations/ConfigPages/RulesPage/rules-page',
                      hasRoute: true,
                      loader: partnerConfigLoader,
                    },
                  ]),
            ],
          },
        ],
        hasRoute: true,
      },
      {
        id: 'cfnol',
        title: 'Conversational FNOL',
        description: 'Configure conversational First Notice of Loss settings',
        url: '/admin/conversational-FNOL',
        icon: MessageSquareText,
        permissions: [SYSTEM_ADMIN, FNOL_UI],
        searchKeywords: [],
        componentPath: '@app/components/Admin/CFNOL/cfnol',
        hasRoute: true,
      },
      {
        id: 'agentic-framework',
        title: 'Agentic Framework',
        description: 'Configure agentic framework settings',
        url: '/admin/agentic-framework',
        icon: MessageSquare,
        permissions: [SYSTEM_ADMIN, IS_ALIAS_OPERATOR],
        searchKeywords: [],
        componentPath:
          '@app/components/Admin/AgenticFramework/agentic-framework',
        hasRoute: true,
        children: [
          {
            id: 'agentic-framework-organization',
            title: 'Organization',
            description: 'Configure agentic framework organization',
            url: '/admin/agentic-framework/organization',
            icon: MessageSquare,
            permissions: [SYSTEM_ADMIN, IS_ALIAS_OPERATOR],
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/AgenticFramework/Organization/agentic-framework-organization',
            hasRoute: true,
          },
          {
            id: 'agentic-framework-agents',
            title: 'Assistants',
            description: 'View and manage all available AI assistants',
            url: '/admin/agentic-framework/assistants',
            icon: MessageSquare,
            permissions: [
              SYSTEM_ADMIN,
              IS_ALIAS_OPERATOR,
              ORG_FABRIC_SETTING_ENABLED,
              CASE_AI_ASSISTANTS,
            ],
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/AgenticFramework/Agents/agents',
            hasRoute: true,
          },
          {
            id: 'agentic-framework-agent-message-prefix',
            title: 'Assistant Message Prefix',
            description:
              'Configure the message prefix for AI assistant messages',
            url: '/admin/agentic-framework/assistant-message-prefix',
            icon: MessageSquare,
            permissions: [
              SYSTEM_ADMIN,
              IS_ALIAS_OPERATOR,
              ORG_FABRIC_SETTING_ENABLED,
              CASE_AI_ASSISTANTS,
            ],
            searchKeywords: [],
            componentPath:
              '@app/components/Admin/AgenticFramework/AgentMessagePrefix/agent-message-prefix',
            hasRoute: true,
          },
        ],
      },
    ],
  },
];

// Constants for feature flag filtering
const REASSIGNMENT_MESSAGE_ITEM_IDS = [
  'claim-reassignment-message',
  'general-reassignment-message',
  'service-reassignment-message',
  'policy-reassignment-message',
  'lob-claim-reassignment-message',
  'lob-general-reassignment-message',
  'lob-policy-reassignment-message',
] as const;

// Helper function to check if reassignment message UI feature flag is enabled
export const isReassignmentMessageUiEnabled = (): boolean =>
  process.env.FEATURE_FLAG_ENABLE_REASSIGNMENT_MESSAGE_UI === 'true';

// Helper function to check if an item should be filtered based on reassignment message feature flag
export const shouldFilterReassignmentMessageItem = (
  itemId: string,
): boolean => {
  if (isReassignmentMessageUiEnabled()) {
    return false;
  }
  return REASSIGNMENT_MESSAGE_ITEM_IDS.includes(
    itemId as (typeof REASSIGNMENT_MESSAGE_ITEM_IDS)[number],
  );
};

// Helper function to check if the case-creation "Send welcome immediately"
// bypass feature flag is enabled. Controls UI visibility of the toggle.
// Note: In production, webpack DefinePlugin injects a boolean directly.
// In tests, process.env values are strings. This handles both cases.
export const isAllowQhBypassOnCaseCreationFeatureFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_ALLOW_QH_BYPASS_ON_CASE_CREATION as unknown as
    | boolean
    | string;
  return flagValue === true || flagValue === 'true';
};

// Helper function to check if internal unread message setting feature flag is enabled
// Note: In production, webpack DefinePlugin injects a boolean directly.
// In tests, process.env values are strings. This handles both cases.
export const isInternalUnreadMessageSettingFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_UNREAD_INTERNAL_MESSAGE_SETTING as unknown as
    | boolean
    | string;
  return flagValue === true || flagValue === 'true';
};

export const isSnoozeDismissFlagOn = (): boolean => {
  const flagValue = process.env.FEATURE_FLAG_SNOOZE_DISMISS as unknown as
    | boolean
    | string;
  return flagValue === true || flagValue === 'true';
};

const AUTO_CLOSE_ITEM_IDS = [
  'claim-auto-close',
  'general-auto-close',
  'service-auto-close',
  'policy-auto-close',
  'lob-claim-auto-close',
  'lob-general-auto-close',
  'lob-service-auto-close',
  'lob-policy-auto-close',
] as const;

const isAutoCloseGlobalEnabled = (): boolean => {
  const flagValue = process.env.FEATURE_FLAG_AUTO_CLOSE_GLOBAL as unknown as
    | boolean
    | string;
  return flagValue === true || flagValue === 'true';
};

export const shouldFilterAutoCloseItem = (itemId: string): boolean => {
  if (isAutoCloseGlobalEnabled()) {
    return false;
  }
  return AUTO_CLOSE_ITEM_IDS.includes(
    itemId as (typeof AUTO_CLOSE_ITEM_IDS)[number],
  );
};

// Read locally rather than via helpers/featureFlags so that admin config stays
// importable from suites that replace that module wholesale with jest.mock.
// Note: In production, webpack DefinePlugin injects a boolean directly.
// In tests, process.env values are strings. This handles both cases.
const isInboundWelcomeMessagesEnabled = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_INBOUND_WELCOME_MESSAGES as unknown as boolean | string;
  return flagValue === true || flagValue === 'true';
};

// The nested Welcome Messages hub introduced with inbound welcome messages. It
// replaces the flat service welcome message items below, so exactly one of the
// two sets is registered depending on the feature flag. Every descendant is
// listed explicitly because getAllAdminItems visits children independently of
// their parent.
const INBOUND_WELCOME_MESSAGES_ITEM_IDS = [
  'service-welcome-messages',
  'service-welcome-outbound',
  'service-welcome-outbound-not-opted-in',
  'service-welcome-outbound-opted-in',
  'service-welcome-outbound-marley-opt-in-disabled',
  'service-welcome-inbound',
  'service-welcome-inbound-not-opted-in',
  'service-welcome-inbound-opted-in',
  'service-welcome-inbound-marley-opt-in-disabled',
] as const;

const LEGACY_SERVICE_WELCOME_MESSAGE_ITEM_IDS = [
  'service-welcome-message-not-opted-in',
  'service-welcome-message-opted-in',
  'service-welcome-message-marley-opt-in-disabled',
] as const;

export const shouldFilterInboundWelcomeMessagesItem = (
  itemId: string,
): boolean => {
  if (isInboundWelcomeMessagesEnabled()) {
    return (
      LEGACY_SERVICE_WELCOME_MESSAGE_ITEM_IDS as readonly string[]
    ).includes(itemId);
  }
  return (INBOUND_WELCOME_MESSAGES_ITEM_IDS as readonly string[]).includes(
    itemId,
  );
};

const FEATURES_QUIET_HOURS_ITEM_ID = 'features-quiet-hours';

// The Features → Quiet Hours page is shown whenever the public QUIET_HOURS
// feature is enabled for the org.
export const shouldFilterFeaturesQuietHoursItem = (
  itemId: string,
  isQuietHoursFeatureEnabled: boolean,
): boolean =>
  itemId === FEATURES_QUIET_HOURS_ITEM_ID && !isQuietHoursFeatureEnabled;

const INTERNAL_FEATURES_ACCESS_ITEM_IDS = [
  'event-logs',
  'internal-features',
] as const;

export const shouldFilterInternalFeaturesAccessItem = (
  itemId: string,
  canAccessInternalFeatures: boolean,
): boolean =>
  (INTERNAL_FEATURES_ACCESS_ITEM_IDS as readonly string[]).includes(itemId) &&
  !canAccessInternalFeatures;

// Utility functions for working with admin config
export const getAllAdminItems = (): AdminItem[] => {
  const items: AdminItem[] = [MAIN_ADMIN_ROUTE];

  const addItemAndChildren = (item: AdminItem) => {
    if (item.children) {
      item.children.forEach(addItemAndChildren);
    }
    if (shouldFilterInboundWelcomeMessagesItem(item.id)) {
      return;
    }
    items.push(item);
  };

  ADMIN_CONFIG.forEach((section) => {
    section.items.forEach(addItemAndChildren);
  });

  return items;
};

export const getAdminItemById = (id: string): AdminItem | undefined =>
  getAllAdminItems().find((item) => item.id === id);

export const getAdminItemByUrl = (url: string): AdminItem | undefined => {
  // Normalize URLs by removing trailing slashes for comparison
  const normalizeUrl = (u: string) => u.replace(/\/+$/, '');
  const normalizedUrl = normalizeUrl(url);

  // Try exact match first (with normalized URLs)
  const exactMatch = getAllAdminItems().find(
    (item) => normalizeUrl(item.url) === normalizedUrl,
  );
  if (exactMatch) return exactMatch;

  // Fallback to dynamic pattern match, supporting paths with :params
  const patternMatch = getAllAdminItems().find((item) => {
    const pattern = item.url;
    if (!pattern.includes(':')) return false;
    const regex = new RegExp(`^${pattern.replace(/:[^/]+/g, '[^/]+')}$`);
    return regex.test(normalizedUrl);
  });

  if (patternMatch) return patternMatch;

  // Special case: match routes with userId parameter
  // e.g., /admin/personal/{userId} matches /admin/personal
  // e.g., /admin/personal/{userId}/profile matches /admin/personal/profile
  // Remove the userId segment and try to match
  if (normalizedUrl.startsWith('/admin/personal/')) {
    // Match pattern: /admin/personal/{userId} or /admin/personal/{userId}/...
    // Remove the userId segment (first segment after /admin/personal/)
    const urlWithoutUserId = normalizedUrl.replace(
      /^\/admin\/personal\/[^/]+/,
      '/admin/personal',
    );

    // Normalize the result to handle trailing slashes
    const normalizedUrlWithoutUserId = normalizeUrl(urlWithoutUserId);

    // Try to match any item (including children) with the URL without userId
    return getAllAdminItems().find(
      (item) => normalizeUrl(item.url) === normalizedUrlWithoutUserId,
    );
  }

  return undefined;
};

/** Returns true if item matches the given search term (title, description, or searchKeywords). When `t` is provided, matches translated title/description. `searchKeywords` are not localized. */
export const itemMatchesSearchTerm = (
  item: AdminItem,
  searchTerm: string,
  descriptionContext?: AdminItemDescriptionContext,
  t?: TranslateFn,
): boolean => {
  const term = searchTerm.toLowerCase().trim();
  const translated = t ? translateAdminItem(item, t) : item;
  const description =
    descriptionContext && item.getDescription
      ? (item.getDescription(descriptionContext) ?? translated.description)
      : translated.description;
  return (
    translated.title.toLowerCase().includes(term) ||
    (description?.toLowerCase().includes(term) ?? false) ||
    (item.searchKeywords?.some((k) => k.toLowerCase().includes(term)) ?? false)
  );
};

export const searchAdminItems = (
  query: string,
  integrationPartnersMap: Record<string, boolean>,
  userPermissions: string[] = [],
  canAccessInternalFeatures: boolean = false,
  descriptionContext?: AdminItemDescriptionContext,
  t?: TranslateFn,
): AdminItem[] => {
  if (!query.trim()) return [];

  const searchTerm = query.toLowerCase().trim();
  const allItems = getAllAdminItems();

  return allItems.filter((item) => {
    // Exclude items with dynamic route parameters in URLs
    if (item.url.includes(':')) {
      return false;
    }

    // Filter out reassignment message items if feature flag is disabled
    if (shouldFilterReassignmentMessageItem(item.id)) {
      return false;
    }

    // Filter out auto-close items if feature flag is disabled
    if (shouldFilterAutoCloseItem(item.id)) {
      return false;
    }

    // Filter out whichever service welcome message UI the feature flag hides
    if (shouldFilterInboundWelcomeMessagesItem(item.id)) {
      return false;
    }

    // Filter out internal-only admin items if user lacks internal features access
    // TODO: Replace hardcoded canAccessInternalFeatures check with IS_MARLEY_USER synthetic permission in admin-config permissions array
    if (
      shouldFilterInternalFeaturesAccessItem(item.id, canAccessInternalFeatures)
    ) {
      return false;
    }

    // Check permissions first
    if (item.permissions && item.permissions.length > 0) {
      const hasAllPermissions = item.permissions.every((permission) =>
        checkPermissionRequirement(
          permission,
          userPermissions,
          integrationPartnersMap,
        ),
      );
      if (!hasAllPermissions) return false;
    }

    return itemMatchesSearchTerm(item, searchTerm, descriptionContext, t);
  });
};

export const getAdminBreadcrumbs = (
  url: string,
): { title: string; url?: string }[] => {
  // Use getAdminItemByUrl to handle userId parameters and other edge cases
  const item = getAdminItemByUrl(url);
  if (!item) return [{ title: 'Admin', url: '/admin' }];

  const allItems = getAllAdminItems();

  const breadcrumbs: { title: string; url?: string }[] = [
    { title: 'Admin', url: '/admin' },
  ];

  // Find parent item if this is a child
  const parentItem = allItems.find((parent) =>
    parent.children?.some((child) => child.id === item.id),
  );

  if (parentItem) {
    // Attempt to resolve parent URL with current params; if not possible, make it non-clickable
    let parentUrl: string | undefined = parentItem.url;

    // Handle userId parameter: if current URL has userId, preserve it in parent URL
    // Use same pattern as getAdminItemByUrl - remove userId segment and check if it matches parent
    if (
      url.startsWith('/admin/personal/') &&
      parentItem.url.startsWith('/admin/personal')
    ) {
      const urlWithoutUserId = url.replace(
        /^\/admin\/personal\/[^/]+/,
        '/admin/personal',
      );
      // If removing userId segment matches parent URL, preserve userId in parent breadcrumb
      if (
        urlWithoutUserId === parentItem.url ||
        urlWithoutUserId.startsWith(`${parentItem.url}/`)
      ) {
        const urlParts = url.split('/');
        if (urlParts.length >= 4) {
          const [, , , userId] = urlParts;
          parentUrl = parentItem.url.replace(
            '/admin/personal',
            `/admin/personal/${userId}`,
          );
        }
      }
    }
    if (parentItem.url.includes(':')) {
      const parentPattern = parentItem.url;
      const names = (parentPattern.match(/:([^/]+)/g) || []).map((n) =>
        n.slice(1),
      );
      const currentParts = url.split('/');
      const patternParts = parentPattern.split('/');
      const paramValues: Record<string, string> = {};
      // Derive params by aligning pattern with current path
      for (let i = 0; i < patternParts.length; i += 1) {
        const [seg] = [patternParts[i]];
        if (seg.startsWith(':') && currentParts[i]) {
          paramValues[seg.slice(1)] = currentParts[i];
        }
      }
      parentUrl =
        names.length > 0
          ? names.reduce(
              (acc, name) =>
                acc.replace(`:${name}`, paramValues[name] || `:${name}`),
              parentPattern,
            )
          : parentPattern;
      // If unresolved params remain, don't link
      if (parentUrl.includes(':')) parentUrl = undefined;
    }
    breadcrumbs.push({ title: parentItem.title, url: parentUrl });
  }

  breadcrumbs.push({ title: item.title });

  return breadcrumbs;
};
